/**
 * The parts of a firefight the flamethrower and the rocket trooper share with every other
 * soldier. `fight.ts` has the same rules inside `stepCombat`, which these two kinds bypass
 * (they do not pick squad roles, take cover or run for help: they bring their weapon to bear).
 */

import { bark } from '../../alert';
import type { Ctx } from '../../ctx';
import { atan2 } from '../../math';
import { ENEMY, secs, TACTICS } from '../../tuning';
import type { Enemy } from '../../types';
import { searchSeconds } from '../fight';
import { travel, turnToward } from '../movement';

/**
 * Reaction delay, taking a downed ranger prisoner, and hunting one who has been lost.
 * Returns true when it has used the tick and the caller's weapon stays quiet.
 */
export function combatPreamble(ctx: Ctx, e: Enemy): boolean {
  const r = ctx.world.ranger;
  const dx = r.x - e.x;
  const dz = r.z - e.z;
  if (e.reactTicks > 0) {
    e.reactTicks--;
    e.moving = false;
    if (e.seesRanger) turnToward(e, atan2(dx, dz), 9);
    return true;
  }
  // A ranger who is down and silent is a prisoner, not a target (02 §9.4) - even to these two.
  if (r.status === 'downed' && r.sinceShot > TACTICS.holdFireAfterTicks) {
    if (dx * dx + dz * dz > 3 * 3) travel(ctx, e, r.x, r.z, ENEMY.walkSpeed * 1.5, 2.5);
    else {
      e.moving = false;
      turnToward(e, atan2(dx, dz), 9);
    }
    return true;
  }
  if (!e.seesRanger && e.lostTicks > ENEMY.lostSightTicks) {
    // To where he was last seen, then search - the same fix for the original's forgetful AI.
    if (travel(ctx, e, e.targetX, e.targetZ, ENEMY.huntSpeed)) {
      e.state = 'search';
      e.stateTicks = secs(searchSeconds(ctx));
      e.path = [];
      bark(ctx, e, 'searching');
    }
    return true;
  }
  return false;
}
